import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import type { SaasApiError, VirtualIpCharacterBatch, VirtualIpCharacterSelectionWorkspace, VirtualIpDraft, VirtualIpVoiceWorkspace } from '../../api'
import { loadVirtualIpWorkflowFacts } from './loadVirtualIpWorkflowFacts'
import type { VirtualIpWorkflowProgress } from './virtualIpProgress'

export type VirtualIpWorkflowContextValue = {
  loading: boolean
  error: string | null
  draft: VirtualIpDraft | null
  selectionWorkspace: VirtualIpCharacterSelectionWorkspace | null
  latestBatch: VirtualIpCharacterBatch | null
  voiceWorkspace: VirtualIpVoiceWorkspace | null
  progress: VirtualIpWorkflowProgress
  refreshProgress: (draftHint?: VirtualIpDraft) => Promise<VirtualIpWorkflowProgress>
}

const FALLBACK_PROGRESS: VirtualIpWorkflowProgress = {
  current_step: 1,
  highest_completed_step: 0,
  furthest_reached_step: 0,
  unlocked_steps: [1],
  completed_steps: [],
  step_states: { 1: 'AVAILABLE' },
  step_state_reasons: {},
  is_complete: false,
}

export const VirtualIpWorkflowContext = createContext<VirtualIpWorkflowContextValue | null>(null)

/** Loads full server workflow facts for the project in the URL — never derive
 * stepper progress from the currently viewed step or page-local partial payloads. */
export function VirtualIpWorkflowProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['virtualIp', 'actions', 'status', 'validation'])
  const { workspaceId = '', projectId = '' } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<VirtualIpDraft | null>(null)
  const [selectionWorkspace, setSelectionWorkspace] = useState<VirtualIpCharacterSelectionWorkspace | null>(null)
  const [latestBatch, setLatestBatch] = useState<VirtualIpCharacterBatch | null>(null)
  const [voiceWorkspace, setVoiceWorkspace] = useState<VirtualIpVoiceWorkspace | null>(null)
  const [progress, setProgress] = useState<VirtualIpWorkflowProgress>(FALLBACK_PROGRESS)

  const applyFacts = useCallback((facts: Awaited<ReturnType<typeof loadVirtualIpWorkflowFacts>>) => {
    setDraft(facts.draft)
    setSelectionWorkspace(facts.selectionWorkspace)
    setLatestBatch(facts.latestBatch)
    setVoiceWorkspace(facts.voiceWorkspace)
    setProgress(facts.progress)
    return facts.progress
  }, [])

  const refreshProgress = useCallback(
    async (draftHint?: VirtualIpDraft) => {
      if (!workspaceId || !projectId) return FALLBACK_PROGRESS
      const facts = await loadVirtualIpWorkflowFacts(workspaceId, draftHint, projectId)
      return applyFacts(facts)
    },
    [applyFacts, workspaceId, projectId],
  )

  useEffect(() => {
    if (!workspaceId || !projectId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    loadVirtualIpWorkflowFacts(workspaceId, undefined, projectId)
      .then((facts) => {
        if (!cancelled) applyFacts(facts)
      })
      .catch((e) => {
        if (!cancelled) setError((e as SaasApiError).message || t('common.loadProgressFailed'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [applyFacts, t, workspaceId, projectId])

  const value = useMemo(
    () => ({
      loading,
      error,
      draft,
      selectionWorkspace,
      latestBatch,
      voiceWorkspace,
      progress,
      refreshProgress,
    }),
    [loading, error, draft, selectionWorkspace, latestBatch, voiceWorkspace, progress, refreshProgress],
  )

  return <VirtualIpWorkflowContext.Provider value={value}>{children}</VirtualIpWorkflowContext.Provider>
}

export function useVirtualIpWorkflowContext(): VirtualIpWorkflowContextValue {
  const ctx = useContext(VirtualIpWorkflowContext)
  if (!ctx) {
    throw new Error('useVirtualIpWorkflowContext must be used within VirtualIpWorkflowProvider')
  }
  return ctx
}

/** Stepper + gates: always use canonical persisted progress from context. */
export function useVirtualIpCanonicalProgress(): VirtualIpWorkflowProgress {
  return useVirtualIpWorkflowContext().progress
}
